/* ==========================================================================
   Hairdealz hair-loss quiz — hd-quiz.js
   Vanilla ES2019, no framework, no build step.

   Structure:
     HQ.config  — step order, step types, branch + exit rules. Edit here to
                  change the flow. All *copy* lives in locales/*.json and is
                  server-rendered by sections/hd-quiz.liquid, so nothing in
                  this file needs translating.
     HQ.state   — answers, mirrored to sessionStorage under `hq_state`.
     HQ.nav     — step navigation, history, progress.
   ========================================================================== */

(function () {

  /* Local currencies (3 Oct 2026): prices on the page are in the visitor's
     currency, so tracking reports that currency and the price tie-break is
     scaled back to EUR. */
  function shopCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  }
  function quizRate() {
    var r = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    return r > 0 ? r : 1;
  }
  'use strict';

  var root = document.querySelector('[data-hq]');
  if (!root) return;

  /* ---------------------------------------------------------------- config */

  var config = {
    storageKey: 'hq_state',
    stateVersion: 1,

    /* The 15 screens, in order. `counted: false` keeps a screen out of the
       progress denominator (exit screens are dead ends, not steps). */
    steps: [
      { id: 'q1',  type: 'single'       },
      { id: 'q2',  type: 'single', branchOn: 'q1' },
      { id: 'q3',  type: 'interstitial' },
      { id: 'q4',  type: 'single'       },
      { id: 'q5',  type: 'single'       },
      { id: 'q6',  type: 'single'       },
      { id: 'q7',  type: 'multi'        },
      { id: 'q8',  type: 'single'       },
      { id: 'q9',  type: 'single'       },
      { id: 'q10', type: 'single'       },
      { id: 'q11', type: 'single'       },
      { id: 'q12', type: 'interstitial' },
      { id: 'q13', type: 'text'         },
      { id: 'q14', type: 'email'        },
      { id: 'q15', type: 'analysis'     },
      { id: 'exit-age', type: 'exit', counted: false },
      { id: 'results',  type: 'results', counted: false }
    ],

    /* Hard exits: answering `value` on `step` sends the visitor to `to` and
       the funnel ends there — no email capture, no product links. */
    exits: [
      { step: 'q4', value: 'under18', to: 'exit-age' }
    ],

    /* Single-select auto-advance delay (ms) — long enough to see the
       selected state, short enough not to feel laggy. */
    advanceDelay: 200,
    analysisDuration: 2800
  };

  var reducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------- state */

  var state = {
    v: config.stateVersion,
    answers: {},
    step: config.steps[0].id,
    startedAt: null
  };

  function loadState() {
    try {
      var raw = window.sessionStorage.getItem(config.storageKey);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.v !== config.stateVersion) return;
      /* Merge into the existing object rather than rebinding it: window.HQ
         holds a reference to this object, and rebinding would leave the
         exported handle pointing at a detached copy. */
      Object.keys(parsed).forEach(function (k) { state[k] = parsed[k]; });
    } catch (e) { /* private mode / quota — run without persistence */ }
  }

  function saveState() {
    try {
      window.sessionStorage.setItem(config.storageKey, JSON.stringify(state));
    } catch (e) { /* no-op */ }
  }

  function setAnswer(stepId, value) {
    state.answers[stepId] = value;
    saveState();
  }

  /* ------------------------------------------------------------------- dom */

  var els = {
    stage: root.querySelector('[data-hq-stage]'),
    steps: {},
    back: root.querySelector('[data-hq-back]'),
    progressFill: root.querySelector('[data-hq-progress-fill]'),
    count: root.querySelector('[data-hq-count]')
  };

  Array.prototype.forEach.call(root.querySelectorAll('[data-hq-step]'), function (el) {
    els.steps[el.getAttribute('data-hq-step')] = el;
  });

  var countedSteps = config.steps.filter(function (s) { return s.counted !== false; });
  var countTemplate = els.count ? els.count.getAttribute('data-template') || '' : '';

  function stepConfig(id) {
    for (var i = 0; i < config.steps.length; i++) {
      if (config.steps[i].id === id) return config.steps[i];
    }
    return null;
  }

  function stepIndex(id) {
    for (var i = 0; i < countedSteps.length; i++) {
      if (countedSteps[i].id === id) return i;
    }
    return -1;
  }

  /* ------------------------------------------------------------ navigation */

  var history_ = [];
  var analysisTimers = [];

  function clearAnalysis() {
    analysisTimers.forEach(function (t) { window.clearTimeout(t); });
    analysisTimers = [];
  }

  function go(id, opts) {
    opts = opts || {};
    var target = els.steps[id];
    if (!target) return;

    /* The analysis screen auto-advances on a timer. Leaving it — including via
       the browser back button — must cancel that timer, or the visitor gets
       yanked to the results a few seconds after navigating away. */
    clearAnalysis();

    Object.keys(els.steps).forEach(function (key) {
      els.steps[key].classList.toggle('is-active', key === id);
      els.steps[key].setAttribute('aria-hidden', key === id ? 'false' : 'true');
    });

    state.step = id;
    saveState();
    document.body.classList.toggle('hq-results', id === 'results');
    root.classList.toggle('is-results', id === 'results');

    /* Expose the current step on the root so a single step can get its own
       layout — the picture question needs a wider stage than the rest. */
    root.className = root.className.replace(/\bis-step-\S+/g, '').trim();
    root.classList.add('is-step-' + id);

    if (opts.push !== false) {
      if (history_[history_.length - 1] !== id) history_.push(id);
      try {
        window.history.pushState({ hq: id }, '', '#' + id);
      } catch (e) { /* no-op */ }
    }

    applyBranch(id);
    if (id === 'results') renderResults();
    updateChrome(id);
    focusStep(target);
    emit('quiz_step', { step_id: id, step_index: stepIndex(id) + 1 });

    if (stepConfig(id) && stepConfig(id).type === 'analysis') runAnalysis();
  }

  function updateChrome(id) {
    var cfg = stepConfig(id);
    var idx = stepIndex(id);
    var isChrome = cfg && cfg.counted !== false;
    var pct = isChrome ? Math.round(((idx + 1) / countedSteps.length) * 100) : 100;

    if (els.progressFill) els.progressFill.style.width = pct + '%';

    var bar = root.querySelector('.hq__progress');
    if (bar) bar.setAttribute('aria-valuenow', String(pct));

    if (els.count) {
      if (isChrome && countTemplate) {
        /* A percentage, not "step 4 of 15": progress made rather than
           questions still to answer. %P% is the slot Liquid left for it. */
        els.count.textContent = countTemplate.replace('%P%', String(pct));
        els.count.hidden = false;
      } else {
        els.count.hidden = true;
      }
    }

    if (els.back) els.back.hidden = history_.length <= 1 || (cfg && cfg.type === 'results');
  }

  function focusStep(target) {
    var heading = target.querySelector('.hq__q, .hq__interstitial-title, h1, h2');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  function nextFrom(id) {
    var exit = config.exits.filter(function (e) {
      return e.step === id && state.answers[id] === e.value;
    })[0];
    if (exit) return exit.to;

    var order = config.steps.filter(function (s) { return s.counted !== false; });
    for (var i = 0; i < order.length; i++) {
      if (order[i].id === id) return order[i + 1] ? order[i + 1].id : 'results';
    }
    return 'results';
  }

  function back() {
    if (history_.length <= 1) return;
    history_.pop();
    if (history_[history_.length - 1] === 'q15') history_.pop();
    var prev = history_[history_.length - 1];
    if (!prev) return;
    go(prev, { push: false });
    try { window.history.replaceState({ hq: prev }, '', '#' + prev); } catch (e) {}
  }

  /* --------------------------------------------------------------- branching
     Q2 renders both the male and the female option sets; the answer to Q1
     decides which one is shown. Keeping both in the DOM means no fetch and
     no layout shift when the visitor goes back and switches gender. */

  function applyBranch(id) {
    var cfg = stepConfig(id);
    if (!cfg || !cfg.branchOn) return;
    var value = state.answers[cfg.branchOn];
    var groups = els.steps[id].querySelectorAll('[data-hq-branch]');
    Array.prototype.forEach.call(groups, function (group) {
      var match = group.getAttribute('data-hq-branch') === value;
      group.hidden = !match;
    });
  }

/* ============================================================== tracking
     Every event goes to the dataLayer (GTM owns Meta and GA4 on this store),
     and also directly to fbq / gtag when they exist, so nothing is lost if a
     tag is misconfigured. Meta only fires for visitors who accepted cookies —
     the pixel sits behind the consent banner — so treat these counts as a
     floor, not a total.
     ============================================================== */

  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  function captureUtms() {
    /* Grab these before the results URL rewrite drops the query string. */
    var params = new URLSearchParams(window.location.search);
    var found = {};
    var any = false;
    UTM_KEYS.forEach(function (k) {
      var v = params.get(k);
      if (v) { found[k] = v; any = true; }
    });
    if (any) {
      state.utm = found;
      saveState();
    }
    return state.utm || {};
  }

  var META_EVENTS = {
    quiz_lead: ['Lead', true],
    quiz_add_to_cart: ['AddToCart', true],
    quiz_complete: ['QuizComplete', false]
  };

  function emit(name, params) {
    params = params || {};
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, params));

    var meta = META_EVENTS[name];
    if (meta && typeof window.fbq === 'function') {
      /* Standard events go through track, the custom one through trackCustom. */
      window.fbq(meta[1] ? 'track' : 'trackCustom', meta[0], {
        content_category: 'hair_quiz',
        value: params.value,
        currency: params.currency || shopCurrency(),
        content_ids: params.recommended_products
      });
    }

    if (typeof window.gtag === 'function') {
      if (name === 'quiz_lead') window.gtag('event', 'generate_lead', params);
      else window.gtag('event', name, params);
    }
  }

  /* =============================================================== Klaviyo
     Client-side, public key only. Three calls, in order:
       1. subscribe (only when the consent box was ticked)
       2. profile properties (always — segmentation is not marketing)
       3. the Completed Hair Quiz event (always — this is analytics)
     An unticked box still produces an event and a profile, never a
     subscription.
     =============================================================== */

  var KLAVIYO_REVISION = '2025-01-15';

  function klaviyoFetch(path, payload) {
    var companyId = settings.klaviyo_company_id;
    if (!companyId) return Promise.resolve(null);
    return window.fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' +
      encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(function () { return null; });
  }

  function needsDoubleOptIn() {
    var list = (settings.klaviyo_doi_countries || '').split(',').map(function (c) {
      return c.trim().toUpperCase();
    }).filter(Boolean);
    var country = (settings.country || '').toUpperCase();
    var locale = (settings.locale || '').toUpperCase();
    return list.indexOf(country) !== -1 || list.indexOf(locale) !== -1;
  }

  function quizProperties(rec) {
    var a = state.answers || {};
    var utm = state.utm || {};
    var products = [rec.core, rec.scalp, rec.support].filter(Boolean);
    return {
      quiz_gender: a.q1 || null,
      quiz_stage: a.q2 || null,
      quiz_stage_label: (copy.stage_label && copy.stage_label[a.q2]) || null,
      quiz_age_band: a.q4 || null,
      quiz_duration: a.q5 || null,
      quiz_prior_minoxidil: a.q6 || null,
      quiz_goals: a.q7 || [],
      quiz_scalp: a.q8 || null,
      quiz_hair_type: a.q9 || null,
      quiz_format_pref: a.q10 || null,
      quiz_priority: a.q11 || null,
      quiz_flag: rec.flag,
      quiz_recommended: products.map(function (p) { return p.handle; }),
      /* Full product rows so a Klaviyo email can render real cards. The event
         only carried handles, which are localised slugs — unusable as copy. */
      quiz_recommended_items: products.map(function (p) {
        var img = p.image || '';
        if (img.indexOf('//') === 0) img = 'https:' + img;
        return {
          slot: p.slot,
          handle: p.handle,
          title: p.title,
          url: p.url.indexOf('http') === 0 ? p.url : window.location.origin + p.url,
          image: img,
          price: p.price / 100,
          price_text: money(p.price),
          months: p.months,
          preorder: !!p.preorder
        };
      }),
      quiz_first_name: (state.answers.q13 || '').trim() || null,
      quiz_recommended_value: products.reduce(function (n, p) { return n + p.price; }, 0) / 100,
      quiz_completed_at: new Date().toISOString(),
      /* Build the shareable URL here rather than reading state.resultsUrl:
         the email is submitted one step before the results render, so that
         field does not exist yet. */
      quiz_results_url: window.location.origin + window.location.pathname +
        '?r=' + encodeAnswers(a) + '#results',
      quiz_locale: settings.locale || null,
      quiz_utm_source: utm.utm_source || null,
      quiz_utm_medium: utm.utm_medium || null,
      quiz_utm_campaign: utm.utm_campaign || null,
      quiz_utm_content: utm.utm_content || null
    };
  }

  function submitToKlaviyo() {
    var a = state.answers || {};
    var answer = a.q14 || {};
    var email = answer.email;
    if (!email) return;

    /* Always recompute. A visitor who goes back, changes an answer and
       submits again would otherwise be filed under the previous routine. */
    var rec = recommend(a);
    state.recommendation = rec;
    var props = quizProperties(rec);
    var firstName = (a.q13 || '').trim();
    var listId = needsDoubleOptIn()
      ? (settings.klaviyo_list_id_doi || settings.klaviyo_list_id)
      : settings.klaviyo_list_id;

    var profileAttrs = { email: email, properties: props };
    if (firstName) profileAttrs.first_name = firstName;

    /* 1 — marketing subscription, only with explicit consent. */
    if (answer.consent && listId) {
      klaviyoFetch('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Hair Quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      });
    }

    /* 2 — profile properties. Segmentation data, not marketing consent. */
    klaviyoFetch('profiles', { data: { type: 'profile', attributes: profileAttrs } });

    /* 3 — the event the flows are built on. */
    klaviyoFetch('events', {
      data: {
        type: 'event',
        attributes: {
          properties: props,
          metric: { data: { type: 'metric', attributes: { name: settings.klaviyo_metric || 'Completed Hair Quiz' } } },
          profile: { data: { type: 'profile', attributes: { email: email } } }
        }
      }
    });

    emit('quiz_lead', {
      consent: !!answer.consent,
      flag: rec.flag,
      value: props.quiz_recommended_value,
      currency: shopCurrency(),
      recommended_products: props.quiz_recommended
    });
  }

  /* ------------------------------------------------------------ interaction */

  function selectSingle(btn, stepId) {
    var group = btn.closest('[data-hq-options]');
    Array.prototype.forEach.call(group.querySelectorAll('.hq__opt'), function (o) {
      o.classList.remove('is-selected');
      o.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');

    var value = btn.getAttribute('data-hq-value');
    setAnswer(stepId, value);
    emit('quiz_answer', { step_id: stepId, value: value });
    if (stepId === 'q1' && !state.startedAt) {
      state.startedAt = new Date().toISOString();
      saveState();
      emit('quiz_start', {});
    }

    window.setTimeout(function () { go(nextFrom(stepId)); },
      reducedMotion ? 0 : config.advanceDelay);
  }

  function toggleMulti(btn, stepId) {
    var selected = state.answers[stepId] || [];
    var value = btn.getAttribute('data-hq-value');
    var i = selected.indexOf(value);
    if (i === -1) selected = selected.concat([value]);
    else selected = selected.slice(0, i).concat(selected.slice(i + 1));

    setAnswer(stepId, selected);
    btn.classList.toggle('is-selected', selected.indexOf(value) !== -1);
    btn.setAttribute('aria-pressed', selected.indexOf(value) !== -1 ? 'true' : 'false');

    var cont = els.steps[stepId].querySelector('[data-hq-continue]');
    if (cont) cont.disabled = selected.length === 0;
  }

  root.addEventListener('click', function (ev) {
    var opt = ev.target.closest('.hq__opt');
    if (opt) {
      var stepEl = opt.closest('[data-hq-step]');
      var stepId = stepEl.getAttribute('data-hq-step');
      var cfg = stepConfig(stepId);
      if (cfg && cfg.type === 'multi') toggleMulti(opt, stepId);
      else selectSingle(opt, stepId);
      return;
    }

    var cont = ev.target.closest('[data-hq-continue]');
    if (cont) {
      var id = cont.closest('[data-hq-step]').getAttribute('data-hq-step');
      if (!validate(id)) return;
      emit('quiz_answer', { step_id: id, value: state.answers[id] });
      go(nextFrom(id));
      return;
    }

    var skip = ev.target.closest('[data-hq-skip]');
    if (skip) {
      var sid = skip.closest('[data-hq-step]').getAttribute('data-hq-step');
      go(nextFrom(sid));
      return;
    }

    if (ev.target.closest('[data-hq-add]')) {
      var rec = state.recommendation || {};
      var picked = [rec.core, rec.scalp, rec.support].filter(Boolean);
      emit('quiz_add_to_cart', {
        value: picked.reduce(function (sum, p) { return sum + p.price; }, 0) / 100,
        currency: shopCurrency(),
        recommended_products: picked.map(function (p) { return p.handle; })
      });
      return;
    }

    var atc = ev.target.closest('[data-hq-atc]');
    if (atc) { addToCart(atc); return; }

    if (ev.target.closest('[data-hq-restart]')) { window.HQ.reset(); return; }

    if (ev.target.closest('[data-hq-back]')) back();
  });

  /* Keyboard: arrow keys move between options within a group. */
  root.addEventListener('keydown', function (ev) {
    if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp' &&
        ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') return;
    var current = document.activeElement;
    if (!current || !current.classList.contains('hq__opt')) return;
    var group = current.closest('[data-hq-options]');
    if (!group) return;
    var opts = Array.prototype.filter.call(group.querySelectorAll('.hq__opt'), function (o) {
      return o.offsetParent !== null;
    });
    var i = opts.indexOf(current);
    var dir = (ev.key === 'ArrowDown' || ev.key === 'ArrowRight') ? 1 : -1;
    var nextEl = opts[(i + dir + opts.length) % opts.length];
    if (nextEl) { nextEl.focus(); ev.preventDefault(); }
  });

  /* --------------------------------------------------------------- validate */

  function validate(stepId) {
    var cfg = stepConfig(stepId);
    if (!cfg) return true;
    var stepEl = els.steps[stepId];

    if (cfg.type === 'multi') {
      return (state.answers[stepId] || []).length > 0;
    }

    if (cfg.type === 'text') {
      var text = stepEl.querySelector('[data-hq-input]');
      setAnswer(stepId, text ? text.value.trim() : '');
      return true; /* optional field */
    }

    if (cfg.type === 'email') {
      var input = stepEl.querySelector('[data-hq-input]');
      var err = stepEl.querySelector('[data-hq-error]');
      var value = input ? input.value.trim() : '';
      var ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
      if (input) input.setAttribute('aria-invalid', ok ? 'false' : 'true');
      if (err) err.classList.toggle('is-visible', !ok);
      if (!ok) { if (input) input.focus(); return false; }
      var consent = stepEl.querySelector('[data-hq-consent]');
      setAnswer(stepId, { email: value, consent: consent ? consent.checked : false });
      submitToKlaviyo();
      return true;
    }

    return true;
  }

  /* --------------------------------------------------------------- analysis */

  function runAnalysis() {
    var stepEl = els.steps['q15'];
    if (!stepEl) return;
    var lines = stepEl.querySelectorAll('.hq__analysis li');
    var total = reducedMotion ? 500 : config.analysisDuration;
    var per = total / (lines.length || 1);

    Array.prototype.forEach.call(lines, function (li, i) {
      li.classList.remove('is-done');
      analysisTimers.push(window.setTimeout(function () {
        li.classList.add('is-done');
      }, per * (i + 1) * 0.8));
    });

    analysisTimers.push(window.setTimeout(function () { go('results'); }, total));
  }

  /* -------------------------------------------------------------- lifecycle */

  window.addEventListener('popstate', function (ev) {
    var id = (ev.state && ev.state.hq) || (window.location.hash || '').replace('#', '');
    if (id === 'q15') { window.history.back(); return; }
    if (id && els.steps[id]) {
      var i = history_.lastIndexOf(id);
      if (i !== -1) history_ = history_.slice(0, i + 1);
      go(id, { push: false });
    }
  });

  window.addEventListener('beforeunload', function () {
    if (state.step !== 'results' && state.startedAt) {
      emit('quiz_abandon', { last_step: state.step });
    }
  });

  function restoreSelections() {
    Object.keys(state.answers).forEach(function (stepId) {
      var stepEl = els.steps[stepId];
      if (!stepEl) return;
      var value = state.answers[stepId];
      Array.prototype.forEach.call(stepEl.querySelectorAll('.hq__opt'), function (o) {
        var v = o.getAttribute('data-hq-value');
        var on = Array.isArray(value) ? value.indexOf(v) !== -1 : value === v;
        o.classList.toggle('is-selected', on);
        o.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var cont = stepEl.querySelector('[data-hq-continue]');
      if (cont && Array.isArray(value)) cont.disabled = value.length === 0;
    });
  }

  function init() {
    loadState();
    captureUtms();
    restoreSelections();

    /* A shared or emailed results link carries the answers in ?r=. */
    var shared = (window.location.search.match(/[?&]r=([^&]+)/) || [])[1];
    var sharedOk = false;
    if (shared) {
      var restored = decodeAnswers(shared);
      /* A real results link always answers q1. Anything else (a stray ?r= from
         another tool, or one Shopify's cached /pages/hair-quiz -> translated-handle
         301 carried over from an earlier request) starts the quiz normally
         instead of showing an empty routine. */
      if (restored && typeof restored === 'object' && restored.q1) {
        state.answers = restored;
        state.step = 'results';
        saveState();
        restoreSelections();
        sharedOk = true;
      }
    }

    var hash = (window.location.hash || '').replace('#', '');
    var start = els.steps[hash] ? hash
      : (els.steps[state.step] ? state.step : config.steps[0].id);
    if (sharedOk) start = 'results';

    /* Rebuild a plausible back-stack so the browser back button works after
       a refresh mid-quiz. */
    var order = config.steps.filter(function (s) { return s.counted !== false; });
    for (var i = 0; i < order.length; i++) {
      history_.push(order[i].id);
      if (order[i].id === start) break;
    }
    if (!els.steps[start]) history_ = [config.steps[0].id];

    go(start, { push: false });
    try { window.history.replaceState({ hq: start }, '', '#' + start); } catch (e) {}
  }


  /* ================================================================ engine
     Deterministic. No network call, no model at runtime — every
     recommendation is traceable to the answers that produced it, and
     `reasons` carries that trace so the results page can show it.
     ================================================================ */

  function readJSON(selector, fallback) {
    /* The data blocks sit next to the quiz, not inside it, so search the
       document rather than the quiz root. */
    var el = document.querySelector(selector);
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }

  var catalog = readJSON('[data-hq-products]', []);
  var settings = readJSON('[data-hq-settings]', {});

  /* Stage -> supply length. Early stages get a 3-month pack, established
     ones a 6-month pack: better value, and it matches the honest timeline. */
  var SUPPLY_BY_STAGE = {
    m1: 3, m2: 3, m3: 6, m4: 6, m5: 6, m6: 6,
    f1: 3, f2: 6, f3: 6, f4: 6
  };

  /* Which brand we want to sell, per gender and per Q11 answer, in order of
     preference. We walk the list and take the first brand that has a product
     the visitor's format answer allows; only if none of them can we fall back
     to open scoring. Pack size within a brand is still chosen by score.

     Kirkland has no foam and Growplex has no dropper, which is why each list
     has a second entry — it is the answer to "what if they asked for the one
     thing this brand does not make". */
  var PREFERRED = {
    male: {
      value:  ['kirkland', 'rogaine', 'foligain'],
      brand:  ['rogaine', 'kirkland'],
      extras: ['growplex', 'foligain']
    },
    female: {
      value:  ['growplex', 'foligain'],
      brand:  ['growplex', 'regaine'],
      extras: ['growplex', 'foligain']
    }
  };

  function eligible(p) {
    if (!p.available) return false;
    if (p.preorder && !settings.include_preorder) return false;
    return true;
  }

  function pickFormat(a) {
    if (a.q10 === 'foam' || a.q10 === 'solution') return a.q10;
    /* No preference: foam is easier on a dry scalp and on hair that is
       washed less often, which is most curly and coily hair. */
    if (a.q8 === 'dry' || a.q9 === 'curly' || a.q9 === 'coily') return 'foam';
    return 'solution';
  }

  function pickStrength(a) {
    if (a.q1 !== 'female') return '5';
    /* Women default to 2%. The licensed once-daily women's 5% foam is only
       eligible when the store owner has switched it on. */
    return '2';
  }

  function recommend(a) {
    a = a || {};
    var out = {
      flag: 'none',
      core: null,
      scalp: null,
      support: null,
      format: null,
      strength: null,
      supply: null,
      reasons: {},
      notes: []
    };

    var stage = a.q2;
    var gender = a.q1;

    /* --- Safety flags. These override the product rules entirely. ------- */
    if (gender === 'female' && stage === 'f3') {
      /* Patchy loss in defined spots is not the pattern minoxidil is designed
         for. No minoxidil, no urgency — scalp care and a GP referral. */
      out.flag = 'patches';
    } else if (gender === 'male' && stage === 'm6') {
      /* Minoxidil works on miniaturised follicles, not where they are gone.
         Maintenance framing only. */
      out.flag = 'advanced';
    }

    var format = pickFormat(a);
    var strength = pickStrength(a);
    var supply = SUPPLY_BY_STAGE[stage] || 3;

    out.format = format;
    out.strength = strength;
    out.supply = supply;
    out.reasons.format = a.q10 === 'none'
      ? (format === 'foam' ? 'auto_foam' : 'auto_solution')
      : 'chosen';

    /* --- Slot 1: core minoxidil ---------------------------------------- */
    if (out.flag !== 'patches') {
      var pool = catalog.filter(function (p) {
        if (p.slot !== 'core') return false;
        if (!eligible(p)) return false;
        if (p.gender !== 'any' && p.gender !== gender) return false;
        if (p.strength === strength) return true;
        /* A 5% product only reaches a woman if it is a foam that is actually
           sold for women: either one tagged unisex5 (sold as for women and
           men), or the licensed women's foam once the store owner has
           switched allow_female_5 on. Never a 5% product meant for men. */
        if (gender === 'female' && p.strength === '5' && p.format === 'foam') {
          if (p.tags.indexOf('unisex5') !== -1) return true;
          if (settings.allow_female_5 && p.tags.indexOf('female5') !== -1) return true;
        }
        return false;
      });

      var brands = (PREFERRED[gender] && PREFERRED[gender][a.q11]) || [];

      pool.forEach(function (p) {
        var score = 0;
        /* Format outranks brand: rule 4 says correct format beats preferred
           brand, and strength is already a hard filter above. */
        if (p.format === format) score += 1000;
        var bi = brands.indexOf(p.brand);
        if (bi !== -1) score += 500 - (bi * 50);
        /* Closest supply length to the stage recommendation. Under-supplying
           is worse than over-supplying: sending someone at an established
           stage away with one month's worth guarantees they run out before
           minoxidil has had a chance to do anything. */
        var months = parseInt(p.months, 10) || 1;
        var gap = months - supply;
        score -= gap < 0 ? -gap * 60 : gap * 40;
        /* A dry or flaky scalp gets the sensitive-skin formulation. */
        if (a.q8 === 'dry' && p.tags.indexOf('sensitive') !== -1) score += 120;
        else if (a.q8 !== 'dry' && p.tags.indexOf('sensitive') !== -1) score -= 40;
        /* Tie-break on price so the cheapest equal match wins. */
        score -= p.price / 100000 / quizRate();
        p._score = score;
      });

      pool.sort(function (x, y) { return y._score - x._score; });

      /* An explicit foam/dropper answer is binding. "No preference" is not an
         answer, so there the preferred brand decides the format. */
      var askedFormat = (a.q10 === 'foam' || a.q10 === 'solution') ? a.q10 : null;

      for (var bi2 = 0; bi2 < brands.length && !out.core; bi2++) {
        var candidates = pool.filter(function (p) {
          return p.brand === brands[bi2] && (!askedFormat || p.format === askedFormat);
        });
        if (candidates.length) {
          out.core = candidates[0];
          out.notes.push('preferred_brand');
          if (bi2 > 0) out.notes.push('brand_substituted');
        }
      }

      if (!out.core) out.core = pool[0] || null;

      if (out.core && !askedFormat) {
        /* Say plainly that the product decided the format, rather than
           claiming we read it off their scalp or hair type. */
        out.format = out.core.format;
        out.reasons.format = 'house_' + out.core.format;
      }

      if (out.core) {
        out.reasons.core = {
          strength_match: out.core.strength === strength,
          format_match: out.core.format === out.format,
          brand_match: brands.indexOf(out.core.brand) === 0,
          supply_match: (parseInt(out.core.months, 10) || 1) === supply
        };
        if (!out.reasons.core.format_match) out.notes.push('format_substituted');
        if (!out.reasons.core.supply_match) out.notes.push('supply_substituted');
      } else {
        out.notes.push('no_core_available');
      }
    }

    /* --- Slot 2: Nizoral ------------------------------------------------ */
    var scalpTag = null;
    if (a.q8 === 'oily' || a.q8 === 'itchy') scalpTag = 'medicated';
    else if (a.q8 === 'dry') scalpTag = 'gentle';

    if (scalpTag) {
      out.scalp = catalog.filter(function (p) {
        return p.slot === 'scalp' && eligible(p) && p.tags.indexOf(scalpTag) !== -1;
      })[0] || null;
      out.reasons.scalp = scalpTag;
      /* A dry, flaky scalp gets the gentler 1% at half the frequency —
         a medicated 2% twice a week would make the flaking worse. */
      out.scalpFrequency = scalpTag === 'gentle' ? 1 : 2;
    }

    /* --- Slot 3: support ------------------------------------------------ */
    if (out.flag !== 'patches') {
      var supportTag = 'roller';
      out.support = catalog.filter(function (p) {
        return p.slot === 'support' && eligible(p) && p.tags.indexOf(supportTag) !== -1;
      })[0] || null;
      out.reasons.support = supportTag;
    }

    return out;
  }

  /* ------------------------------------------------------------- harness
     Sweeps every answer combination that can change the product selection
     and asserts the rules in the brief. Run HQ.testAll() in the console. */

  function testAll() {
    var genders = ['male', 'female'];
    var stages = { male: ['m1','m2','m3','m4','m5','m6'], female: ['f1','f2','f3','f4'] };
    var ages = ['18_24','25_34','35_44','45_54','55_plus'];
    var durations = ['lt6m','6m_2y','2_5y','gt5y'];
    var priors = ['never','current','lapsed'];
    var scalps = ['dry','oily','itchy','balanced'];
    var hair = ['straight','wavy','curly','coily'];
    var formats = ['foam','solution','none'];
    var priorities = ['value','brand','extras'];

    var total = 0;
    var failures = [];
    var noCore = [];
    var seen = {};
    var flagCounts = { none: 0, patches: 0, advanced: 0 };
    var substitutions = { format: 0, brand: 0, supply: 0 };
    var house = 0;
    var preorderShown = 0;
    var seenBrand = {};

    function fail(msg, a) { if (failures.length < 25) failures.push({ why: msg, answers: a }); }

    genders.forEach(function (g) {
      stages[g].forEach(function (st) {
        ages.forEach(function (age) {
          durations.forEach(function (dur) {
            priors.forEach(function (pri) {
              scalps.forEach(function (sc) {
                hair.forEach(function (h) {
                  formats.forEach(function (fm) {
                    priorities.forEach(function (pr) {
                      var a = { q1: g, q2: st, q4: age, q5: dur, q6: pri,
                                q7: ['regrow'], q8: sc, q9: h, q10: fm, q11: pr };
                      var r = recommend(a);
                      total++;
                      flagCounts[r.flag]++;

                      /* (a) exactly one core product unless a flag fires */
                      if (r.flag === 'patches') {
                        if (r.core) fail('patches flag still returned a minoxidil product', a);
                        if (r.support) fail('patches flag returned a support product', a);
                      } else if (!r.core) {
                        if (noCore.length < 25) noCore.push(a);
                      }

                      /* (b) never recommend something unavailable or pre-order */
                      [r.core, r.scalp, r.support].forEach(function (p) {
                        if (!p) return;
                        seen[p.handle] = (seen[p.handle] || 0) + 1;
                        if (!p.available) fail('recommended an unavailable product: ' + p.handle, a);
                        if (p.preorder && !settings.include_preorder) {
                          fail('recommended a pre-order product while they are switched off: ' + p.handle, a);
                        }
                        if (p.preorder) preorderShown++;
                      });

                      /* (c) a woman only ever gets a 5% product that is sold for
                         women: a unisex5 foam, or the licensed women's foam once
                         allow_female_5 is on. Never a men's 5% product. */
                      if (g === 'female' && r.core && r.core.strength === '5') {
                        var okUnisex = r.core.format === 'foam' && r.core.tags.indexOf('unisex5') !== -1;
                        var okLicensed = settings.allow_female_5 && r.core.tags.indexOf('female5') !== -1;
                        if (!okUnisex && !okLicensed) {
                          fail('female was recommended a 5% product she should not get: ' + r.core.handle, a);
                        }
                      }
                      /* men are never dropped to 2% */
                      if (g === 'male' && r.core && r.core.strength !== '5') {
                        fail('male was recommended a ' + r.core.strength + '% product', a);
                      }

                      /* (d) flags fire on exactly the right stages */
                      if (st === 'f3' && r.flag !== 'patches') fail('f3 did not raise the patches flag', a);
                      if (st === 'm6' && r.flag !== 'advanced') fail('m6 did not raise the advanced flag', a);
                      if (st !== 'f3' && st !== 'm6' && r.flag !== 'none') fail('unexpected flag ' + r.flag, a);

                      /* scalp rules */
                      if (sc === 'balanced' && r.scalp) fail('balanced scalp got a Nizoral', a);
                      if ((sc === 'oily' || sc === 'itchy') && !r.scalp) fail('oily/itchy scalp got no Nizoral', a);

                      if (r.core) seenBrand[r.core.brand] = (seenBrand[r.core.brand] || 0) + 1;
                      if (r.core && r.notes.indexOf('preferred_brand') !== -1) house++;
                      if (r.notes.indexOf('format_substituted') !== -1) substitutions.format++;
                      if (r.notes.indexOf('brand_substituted') !== -1) substitutions.brand++;
                      if (r.notes.indexOf('supply_substituted') !== -1) substitutions.supply++;
                    });
                  });
                });
              });
            });
          });
        });
      });
    });

    return {
      combinations: total,
      failures: failures.length,
      failureSamples: failures,
      noCoreCount: noCore.length,
      noCoreSamples: noCore.slice(0, 5),
      flagCounts: flagCounts,
      substitutions: substitutions,
      brandMix: seenBrand,
      preferredBrandWins: house,
      preorderRecommended: preorderShown,
      productsUsed: seen,
      catalogSize: catalog.length,
      settings: settings
    };
  }


  /* =============================================================== results
     The results are a state of this same section, not a separate page, so
     the answers are still in memory. The `?r=` parameter carries a
     base64-encoded copy of the answers, which makes the URL shareable and
     re-openable from the follow-up email.
     =============================================================== */

  var copy = readJSON('[data-hq-copy]', {});

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function fill(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (m, key) {
      return vars[key] != null ? vars[key] : '';
    });
  }

  /* The shop's money_format contains an HTML wrapper (a currency app sets it),
     so anything built from it must be written with innerHTML, never
     textContent — otherwise the customer sees the tags. */
  function money(cents) {
    var fmt = settings.money_format || '€{{amount}}';
    /* Local currencies use formats this fallback does not know (SEK:
       {{amount_with_space_separator}}); the theme's formatMoney does. */
    if (typeof window.formatMoney === 'function') {
      try { return window.formatMoney(cents, fmt); } catch (e) {}
    }
    var amount = (cents / 100);
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount.toFixed(2))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.toFixed(2).replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(amount)))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(amount)));
  }

  function encodeAnswers(answers) {
    try {
      /* q14 (email + consent) never travels in a URL: this string ends up in
         the address bar, in the Klaviyo profile property `quiz_results_url`,
         and in any link the visitor forwards. Name (q13) stays so a reopened
         result is still personal. Dropping it here also means a decoded link
         renders no "we sent it to <email>" line, which is correct — reopening
         a link is not a fresh submission. */
      var safe = {};
      Object.keys(answers || {}).forEach(function (k) {
        if (k !== 'q14') safe[k] = answers[k];
      });
      return window.btoa(unescape(encodeURIComponent(JSON.stringify(safe))))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) { return ''; }
  }

  function decodeAnswers(str) {
    try {
      var b = str.replace(/-/g, '+').replace(/_/g, '/');
      while (b.length % 4) b += '=';
      var parsed = JSON.parse(decodeURIComponent(escape(window.atob(b))));
      /* Links minted before the strip above still carry q14. Drop it on the
         way in too, so a forwarded link never seeds the sender's address into
         this visitor's state. */
      if (parsed && typeof parsed === 'object') delete parsed.q14;
      return parsed;
    } catch (e) { return null; }
  }

  function coreWhy(rec, a) {
    var lines = [];
    if (a.q1 === 'female') {
      if (rec.core.strength !== '5') {
        lines.push(copy.why.core_strength_female);
      } else if (rec.core.tags.indexOf('female5') !== -1) {
        lines.push(copy.why.core_strength_female5);
      } else {
        /* A unisex 5% foam is not the same claim as the licensed women's
           product — do not describe it as one. */
        lines.push(copy.why.core_strength_female5_unisex);
      }
    } else {
      lines.push(copy.why.core_strength_male);
    }
    if (rec.reasons.core && rec.reasons.core.brand_match) {
      if (a.q11 === 'value') lines.push(copy.why.core_brand_value);
      else if (a.q11 === 'brand') lines.push(copy.why.core_brand_known);
      else if (a.q11 === 'extras') lines.push(copy.why.core_brand_extras);
    }
    if (rec.core.tags.indexOf('sensitive') !== -1) lines.push(copy.why.core_sensitive);
    /* Three months is the honest minimum evaluation window, so only flag a
       pack shorter than that as "the start rather than the test". A 3-month
       pack given to someone we would have liked to sell 6 months to is still
       a real trial, not a stub. */
    var months = parseInt(rec.core.months, 10) || 1;
    lines.push(months < 3
      ? fill(copy.why.core_supply_short, { months: rec.core.months })
      : fill(copy.why.core_supply, { months: rec.core.months }));
    return lines;
  }

  /* Add to cart, straight from a result card. The quiz page ships without the
     header (theme.liquid skips header-group on this template), so there is no
     cart drawer and no cart-count to update — the button reports success on
     itself instead. Sold out keeps the same shape, inverted: white inside,
     green outline. */
  function cardAtc(product) {
    var price = '<span class="hq-card__atc-price">' + money(product.price) + '</span>';

    if (!product.available) {
      return '<span class="hq-card__atc hq-card__atc--soldout" aria-disabled="true">' +
        '<span>' + esc(copy.card_sold_out) + '</span>' + price + '</span>';
    }

    var label = product.preorder ? copy.card_preorder : copy.card_atc;
    return '<button type="button" class="hq-card__atc" data-hq-atc="' + esc(product.variant_id) +
      '" data-hq-atc-handle="' + esc(product.handle) + '" data-hq-atc-price="' + esc(product.price) + '">' +
      '<span class="hq-card__atc-label">' + esc(label) + '</span>' + price + '</button>';
  }

  function addToCart(btn) {
    if (btn.getAttribute('data-hq-busy') === '1') return;
    btn.setAttribute('data-hq-busy', '1');
    btn.classList.add('is-loading');

    var url = (settings.cart_add_url || '/cart/add.js');
    /* /cart/add returns HTML, /cart/add.js returns JSON — the route helper
       gives the former, so ask for the JS variant explicitly. */
    if (url.indexOf('.js') === -1) url += '.js';

    window.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(btn.getAttribute('data-hq-atc')), quantity: 1 }] })
    })
      .then(function (res) { return res.ok ? res.json() : Promise.reject(res); })
      .then(function () {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        /* The button keeps its label and price — the confirmation sentence is
           a full sentence in every locale ("Product added to cart
           successfully") and would wrap the button to three lines. It goes
           under the button instead. */
        var wrap = btn.parentNode;
        var note = wrap.querySelector('.hq-card__added');
        if (!note) {
          note = document.createElement('p');
          note.className = 'hq-card__added';
          note.setAttribute('role', 'status');
          wrap.appendChild(note);
        }
        note.textContent = copy.card_added || '';
        emit('quiz_add_to_cart', {
          value: (Number(btn.getAttribute('data-hq-atc-price')) || 0) / 100,
          currency: shopCurrency(),
          recommended_products: [btn.getAttribute('data-hq-atc-handle')]
        });
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        /* Nothing landed in the cart, so do not claim it did — send the
           visitor to the product page instead of failing silently. */
        var card = btn.closest('.hq-card');
        var link = card && card.querySelector('.hq-card__title a');
        if (link) window.location.href = link.getAttribute('href');
      })
      .then(function () { btn.removeAttribute('data-hq-busy'); });
  }

  function productCard(n, product, whyLines, howto, extra) {
    var price = product.compare_at > product.price
      ? '<span class="hq-card__price">' + money(product.price) + '</span>' +
        '<s class="hq-card__was">' + money(product.compare_at) + '</s>'
      : '<span class="hq-card__price">' + money(product.price) + '</span>';

    return '' +
      '<li class="hq-card">' +
        '<div class="hq-card__media">' +
          (product.image
            ? '<img src="' + esc(product.image) + '" alt="' + esc(product.title) + '" width="200" height="200" loading="lazy" decoding="async">'
            : '') +
        '</div>' +
        '<div class="hq-card__body">' +
          '<span class="hq-card__step">' + esc(fill(copy.step_label, { n: n })) + '</span>' +
          '<h3 class="hq-card__title"><a href="' + esc(product.url) + '">' + esc(product.title) + '</a></h3>' +
          '<div class="hq-card__prices">' + price + '</div>' +
          (extra ? '<p class="hq-card__flagnote">' + esc(extra) + '</p>' : '') +
          '<ul class="hq-card__why">' +
            whyLines.filter(Boolean).map(function (l) { return '<li>' + esc(l) + '</li>'; }).join('') +
          '</ul>' +
          (howto ? '<p class="hq-card__howto">' + esc(howto) + '</p>' : '') +
          '<div class="hq-card__buy">' + cardAtc(product) + '</div>' +
        '</div>' +
      '</li>';
  }

  function buildTimeline(a) {
    var t = copy.timeline || {};
    var first = a.q6 === 'current' ? t.t1_body_current : t.t1_body;
    var third = (a.q5 === 'gt5y' || a.q5 === '2_5y') ? t.t3_body_long : t.t3_body;
    var rows = [
      [t.t1_title, first],
      [t.t2_title, t.t2_body],
      [t.t3_title, third],
      [t.t4_title, t.t4_body]
    ];
    return '<ol class="hq-timeline">' + rows.map(function (r) {
      return '<li><span class="hq-timeline__when">' + esc(r[0]) + '</span>' +
             '<span class="hq-timeline__what">' + esc(r[1]) + '</span></li>';
    }).join('') + '</ol>';
  }

  function cartPermalink(products) {
    var items = products.filter(Boolean).map(function (p) { return p.variant_id + ':1'; });
    if (!items.length) return null;
    var base = (settings.cart_url || '/cart') + '/' + items.join(',');
    var code = settings.discount_code;
    return code ? base + '?discount=' + encodeURIComponent(code) : base;
  }

  function renderResults() {
    var host = root.querySelector('[data-hq-results]');
    if (!host) return;

    var a = state.answers || {};
    var rec = recommend(a);
    state.recommendation = rec;
    saveState();

    var name = (a.q13 || '').trim();
    var email = (a.q14 && a.q14.email) || '';
    var goals = a.q7 || [];
    var products = [rec.core, rec.scalp, rec.support].filter(Boolean);

    var chips = [];
    if (a.q1 && copy.chip_gender) chips.push(copy.chip_gender[a.q1]);
    if (a.q2 && copy.stage_label) chips.push(copy.stage_label[a.q2]);
    if (a.q4 && copy.age) chips.push(copy.age[a.q4]);
    if (goals[0] && copy.goal) chips.push(copy.goal[goals[0]]);

    var html = '';

    /* 1 — personalised headline + profile echo */
    html += '<header class="hq-res__head">' +
      '<h1 class="hq-res__title">' +
        esc(name ? fill(copy.headline_named, { name: name }) : copy.headline) +
      '</h1>' +
      '<p class="hq-res__sub">' + esc(copy.sub) + '</p>' +
      '<ul class="hq-res__chips">' +
        chips.filter(Boolean).map(function (c) { return '<li>' + esc(c) + '</li>'; }).join('') +
      '</ul>' +
      (email ? '<p class="hq-res__sent">' + esc(fill(copy.email_sent, { email: email })) + '</p>' : '') +
      '</header>';

    /* A scale showing where their answer sits, because "hairline and crown"
       means little on its own. Six positions for men, four for women, in the
       same order the question offered them. */
    var scale = a.q1 === 'female' ? ['f1', 'f2', 'f4', 'f3'] : ['m1', 'm2', 'm3', 'm4', 'm5', 'm6'];
    var here = scale.indexOf(a.q2);
    if (here !== -1) {
      html += '<section class="hq-res__block hq-stagemeter">' +
        '<span class="hq-stagemeter__title">' + esc(copy.stage_meter_title) + '</span>' +
        '<ol class="hq-stagemeter__track">' +
          scale.map(function (key, i) {
            var cls = i < here ? ' is-past' : (i === here ? ' is-here' : '');
            return '<li class="hq-stagemeter__seg' + cls + '">' +
                   (i === here ? '<span class="hq-stagemeter__pin">' +
                      esc(copy.stage_label[key]) + '</span>' : '') +
                   '</li>';
          }).join('') +
        '</ol>' +
        '<div class="hq-stagemeter__ends">' +
          '<span>' + esc(copy.stage_meter_early) + '</span>' +
          '<span>' + esc(copy.stage_meter_late) + '</span>' +
        '</div>' +
        '</section>';
    }

    /* Stage line + why this format */
    var stageLine = copy.stage ? copy.stage[a.q2] : '';
    html += '<section class="hq-res__block hq-res__context">' +
      (stageLine ? '<p>' + esc(stageLine) + '</p>' : '') +
      (rec.core && copy.format_reason
        ? '<p>' + esc(copy.format_reason[rec.reasons.format === 'chosen'
            ? 'chosen_' + rec.format
            : rec.reasons.format]) + '</p>'
        : '') +
      '</section>';

    /* 2 — safety flag copy, ahead of anything that looks like a sell */
    if (rec.flag === 'patches') {
      html += '<section class="hq-res__block hq-res__flag">' +
        '<h2>' + esc(copy.flag_patches_title) + '</h2>' +
        '<p>' + esc(copy.flag_patches_body) + '</p></section>';
    } else if (rec.flag === 'advanced') {
      html += '<section class="hq-res__block hq-res__flag">' +
        '<h2>' + esc(copy.flag_advanced_title) + '</h2>' +
        '<p>' + esc(copy.flag_advanced_body) + '</p></section>';
    }

    /* 3 — the routine */
    if (products.length) {
      var n = 0;
      var cards = '';
      if (rec.core) {
        n++;
        var howtoCore = rec.core.format === 'foam' ? copy.howto.core_foam : copy.howto.core_solution;
        cards += productCard(n, rec.core, coreWhy(rec, a), howtoCore,
          rec.core.preorder ? copy.preorder_note : null);
      }
      if (rec.scalp) {
        n++;
        var scalpWhy = [rec.reasons.scalp === 'gentle' ? copy.why.scalp_gentle : copy.why.scalp_medicated];
        var scalpHow = rec.scalpFrequency === 1 ? copy.howto.scalp_1 : copy.howto.scalp_2;
        cards += productCard(n, rec.scalp, scalpWhy, scalpHow);
      }
      if (rec.support) {
        n++;
        cards += productCard(n, rec.support, [copy.why.support_roller], copy.howto.support_roller);
      }
      html += '<section class="hq-res__block">' +
        '<h2 class="hq-res__h2">' + esc(copy.routine_title) + '</h2>' +
        '<ol class="hq-cards">' + cards + '</ol></section>';
    }

    /* 4 — the shed, explained before the sell, not after the refund request */
    if (rec.core && (a.q6 === 'never' || a.q6 === 'lapsed')) {
      html += '<section class="hq-res__block hq-res__shed">' +
        '<h2 class="hq-res__h2">' + esc(copy.shedding_title) + '</h2>' +
        '<p>' + esc(a.q6 === 'lapsed' ? copy.shedding_body_lapsed : copy.shedding_body) + '</p>' +
        '</section>';
    }

    /* 5 — what to expect */
    if (rec.core) {
      html += '<section class="hq-res__block">' +
        '<h2 class="hq-res__h2">' + esc(copy.timeline_title) + '</h2>' +
        buildTimeline(a) + '</section>';
    }

    /* 6 — one tap to a filled cart. No countdown, no fake scarcity. */
    var link = cartPermalink(products);
    if (link) {
      var total = products.reduce(function (sum, p) { return sum + p.price; }, 0);
      html += '<section class="hq-res__block hq-res__cta">' +
        '<h2 class="hq-res__h2">' + esc(copy.cta_title) + '</h2>' +
        '<a class="hq__btn hq__btn--solid" href="' + esc(link) + '" data-hq-add>' +
          fill(esc(copy.cta_add), { total: money(total) }) +
        '</a>' +
        (settings.discount_code
          ? '<p class="hq-res__discount">' + esc(fill(copy.discount_note, { code: settings.discount_code })) + '</p>'
          : '') +
        '<a class="hq__skip" href="' + esc(products[0].url) + '">' + esc(copy.cta_browse) + '</a>' +
        '</section>';
    }

    /* 7 — trust, then the small print, once */
    html += '<section class="hq-res__block hq-res__trust">' +
      '<h2 class="hq-res__h2">' + esc(copy.trust_title) + '</h2>' +
      '<ul>' + (copy.trust || []).map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('') + '</ul>' +
      '</section>';

    html += '<p class="hq-res__smallprint">' + esc(copy.small_print) + '</p>';
    html += '<p class="hq-res__restart"><button type="button" class="hq__skip" data-hq-restart>' +
            esc(copy.restart) + '</button></p>';

    host.innerHTML = html;

    /* Shareable URL: answers travel in ?r= so the same result reopens in a
       fresh browser and from the follow-up email. */
    var encoded = encodeAnswers(a);
    if (encoded) {
      try {
        var url = window.location.pathname + '?r=' + encoded + '#results';
        window.history.replaceState({ hq: 'results' }, '', url);
        state.resultsUrl = window.location.origin + url;
        saveState();
      } catch (e) { /* no-op */ }
    }

    emit('quiz_complete', {
      flag: rec.flag,
      recommended_products: products.map(function (p) { return p.handle; })
    });
  }

  /* Expose for the Phase 3 test harness and for debugging. */
  window.HQ = {
    config: config,
    state: state,
    catalog: catalog,
    settings: settings,
    go: go,
    setAnswer: setAnswer,
    recommend: recommend,
    testAll: testAll,
    renderResults: renderResults,
    reset: function () {
      try { window.sessionStorage.removeItem(config.storageKey); } catch (e) {}
      state.answers = {};
      state.step = config.steps[0].id;
      state.startedAt = null;
      state.recommendation = null;
      state.resultsUrl = null;
      history_ = [];
      /* Drop the ?r= results payload from the URL first: init() reads it, so
         leaving it in place would bounce "start again" straight back to the
         results the visitor just asked to leave. */
      try {
        window.history.replaceState({}, '', window.location.pathname);
      } catch (e) { /* no-op */ }
      init();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
